// GA4 tracking helpers. The SPA does client-side routing, so pageviews must be
// sent manually on every history change — the gtag snippet in home.php only
// fires once on initial load.
const GA4_MEASUREMENT_ID = "G-F4KZHDWJEE";
// Dev-only routing logs. CRA replaces process.env.NODE_ENV at build time, so the
// production bundle compiles this to false and ships zero [language-routing] noise.
const ROUTING_DEBUG = process.env.NODE_ENV !== "production";

const routingLog = (...args) => {
  if (ROUTING_DEBUG && typeof console !== "undefined") {
    console.info("[language-routing]", ...args);
  }
};

const hasGtag = () => typeof window !== "undefined" && typeof window.gtag === "function";

const originalHistoryMethods = new WeakMap();

export const replaceHistoryWithoutLanguage = (history, location, state) => {
  const stored = originalHistoryMethods.get(history) || {};
  return (stored.replace || history.replace)(location, state);
};

export const getCurrentLang = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("lang") === "es") return "es";
    return localStorage.getItem("lang") === "es" ? "es" : "en";
  } catch (e) {
    return "en";
  }
};

// Set as a user property on every pageview so "Spanish users" is a GA4 segment
// deduplicated by user, rather than a count of toggle actions. Keep these values
// stable ("spanish"/"english") — GA4 user properties are not backfilled, so a
// rename splits the user-scoped Language dimension and breaks past segments.
// The event-scoped page_language param uses "es"/"en"; scopes are independent.
const syncLanguageProperty = (lang) => {
  if (!hasGtag()) return;
  window.gtag("set", "user_properties", {
    language: lang === "es" ? "spanish" : "english",
  });
};

export const trackPageView = (location) => {
  if (!hasGtag()) return;
  try {
    const loc = location || window.location;
    const path = `${loc.pathname}${loc.search || ""}`;
    const lang = getCurrentLang();
    routingLog("pageview", { path, lang });
    syncLanguageProperty(lang);
    window.gtag("event", "page_view", {
      send_to: GA4_MEASUREMENT_ID,
      page_path: path,
      page_location: `${window.location.origin}${path}`,
      page_title: document.title,
      // Register this event parameter as "Page language" in GA4.
      // It lets route reports filter Spanish pageviews without relying on ?lang=es.
      page_language: lang,
    });
  } catch (e) {
    console.warn("GA4 pageview tracking failed:", e);
  }
};

export const trackEvent = (eventName, params = {}) => {
  if (!hasGtag()) return;
  try {
    const lang = getCurrentLang();
    window.gtag("event", eventName, {
      send_to: GA4_MEASUREMENT_ID,
      page_language: lang,
      // Deliberate: ...params comes last, so a caller MAY override
      // page_language (the kept analytics.test.js documents this). Only do it on purpose.
      ...params,
    });
  } catch (e) {
    console.warn(`GA4 event "${eventName}" failed:`, e);
  }
};

// Attaches the route-change listener and sends the initial pageview.
export const initPageViewTracking = (history) => {
  trackPageView(history.location);
  return history.listen((location) => trackPageView(location));
};

// Adds ?lang=es to a path when Spanish is active, so the language is visible in
// GA4's page_location and every URL-based report can be split by language.
// Leaves English URLs untouched (en is the default, no param needed).
export const withLangParam = (path, lang = getCurrentLang()) => {
  if (typeof path !== "string" || lang !== "es") return path;
  // Don't touch absolute URLs or non-route values.
  if (/^[a-z]+:\/\//i.test(path)) return path;

  const [beforeHash, ...hashParts] = path.split("#");
  const hash = hashParts.length ? `#${hashParts.join("#")}` : "";
  const [pathname, ...queryParts] = beforeHash.split("?");
  const query = queryParts.join("?");

  const params = new URLSearchParams(query);
  if (params.get("lang") === "es") return path;
  params.set("lang", "es");

  return `${pathname}?${params.toString()}${hash}`;
};

const EXCLUDED_PATH_PREFIXES = ["/admin"];
const isExcludedPath = (pathname) =>
  EXCLUDED_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));

// Wraps history.push/replace so the active language rides along on every
// public client-side navigation without touching the ~45 call sites
// individually. While Spanish is active, ?lang=es is added to every
// destination except the excluded admin paths.
export const applyLangToHistory = (history) => {
  ["push", "replace"].forEach((method) => {
    const original = history[method].bind(history);
    const originals = originalHistoryMethods.get(history) || {};
    originals[method] = original;
    originalHistoryMethods.set(history, originals);
    history[method] = (location, state) => {
      const pathname =
        typeof location === "string"
          ? location.split(/[?#]/)[0]
          : location && location.pathname;
      const lang = getCurrentLang();
      if (pathname && isExcludedPath(pathname)) {
        routingLog("navigation skipped (excluded path)", { method, pathname, lang });
        return original(location, state);
      }
      routingLog("navigation", {
        method,
        from: history.location && `${history.location.pathname}${history.location.search || ""}`,
        requested: location,
        lang,
      });
      if (lang !== "es") {
        return original(location, state);
      }
      if (typeof location === "string") {
        const nextLocation = withLangParam(location, lang);
        routingLog("navigation rewritten", { method, from: location, to: nextLocation });
        return original(nextLocation, state);
      }
      if (location && typeof location === "object") {
        const merged = withLangParam(`${location.pathname || ""}${location.search || ""}`, lang);
        const [nextPathname, ...searchParts] = merged.split("?");
        const nextSearch = searchParts.join("?");
        const nextLocation = { ...location, pathname: nextPathname, search: nextSearch ? `?${nextSearch}` : "" };
        routingLog("navigation rewritten", { method, from: location, to: nextLocation });
        return original(nextLocation, state);
      }
      return original(location, state);
    };
  });
  return history;
};

// A returning Spanish user (localStorage, no ?lang=es) can land on a bare URL:
// bookmark, typed domain, or a link without the param. Normalize that first URL
// so URL-based route reports include the landing page too. Call this AFTER
// applyLangToHistory (uses the bypass) and BEFORE initPageViewTracking (so the
// replace does not emit a second pageview). Returns true when the URL changed.
export const normalizeInitialLanguage = (history) => {
  const { pathname = "", search = "", hash = "" } = history.location;
  const current = `${pathname}${search}`;
  const normalized = withLangParam(current);
  if (normalized === current) return false;
  const [nextPathname, ...searchParts] = normalized.split("?");
  const nextSearch = searchParts.join("?");
  replaceHistoryWithoutLanguage(history, {
    pathname: nextPathname,
    search: nextSearch ? `?${nextSearch}` : "",
    hash,
  });
  return true;
};
